import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { useXssGetFeatureSwitchesQuery } from './getFeatureSwitches.generated';
import { FEATURE_SWITCHES } from '@config/app.constants';

type FeatureSwitches = ReturnType<typeof useXssGetFeatureSwitchesQuery>['data'];
type FetcherProps = {
  intl: IntlShape;
  WrappedComponent: React.ComponentType<Omit<FetcherProps, 'WrappedComponent'>>;
  [x: string]: unknown;
};

const FEATURES_PREFIX = /(^xts_|^xss_)\w+/;
const RECRUITING_SETTINGS_FLAG = 'recruiting_settings_your_settings';

const extractFeatureSwitches = (data: FeatureSwitches) => {
  const { featureSwitches: features } = data?.viewer || {};
  const { XING_REBRANDING_ENABLED } = FEATURE_SWITCHES;

  return features
    ? features.filter(
        (feature) =>
          feature.match(FEATURES_PREFIX) ||
          feature === RECRUITING_SETTINGS_FLAG ||
          feature === XING_REBRANDING_ENABLED,
      )
    : [];
};

const FeatureFlagFetcher = ({
  intl,
  WrappedComponent,
  ...props
}: FetcherProps) => {
  const { data, loading } = useXssGetFeatureSwitchesQuery();

  if (loading) return null;

  const flags = extractFeatureSwitches(data);

  if (!window?.featureSwitches) {
    window.featureSwitches = flags;
  }

  return <WrappedComponent intl={intl} featureSwitches={flags} {...props} />;
};

const withFeatures = (Component: React.ComponentType) =>
  injectIntl(({ intl, ...props }) => {
    return (
      <FeatureFlagFetcher intl={intl} WrappedComponent={Component} {...props} />
    );
  });

export { withFeatures };
